import React, { Fragment, useContext, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import BreadCrumb from '../../../components/common/BreadCrumb';
import commonArray from '../../../components/common/commonArray';
import Spinner from '../../../components/common/spinner';
import { GlobalContext, handleError } from '../../../context/Provider';
import henceforthApi from '../../../utils/henceforthApi';
import { MultipleSelect } from '../productInterface';
import { Button } from '../../../components/ui/button';
import { Layers, Save, AlertCircle } from 'lucide-react';

const EdiSpecification: React.FC = () => {
  const location = useLocation();
  const match = useMatch('product/:id/specification/:specification_id/edit');
  const { language_data, authState, toastMessage, authDispatch } = useContext(GlobalContext);

  const breadCrumbPath = [
    {
      name: `${language_data?.[authState.lang]?.common_home || 'Home'}`,
      url: `/`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_list || 'List'
        }`,
      url: `/products/1`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_deatil || 'Detail'
        }`,
      url: `/product/${match?.params.id}`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_edit || 'Edit'} ${language_data?.[authState.lang]?.common_specifications || 'Specification'
        }`,
      url: ``,
      active: 'not-allowed',
    },
  ];

  const newParam = new URLSearchParams(location.search);
  const [editSpecs, setEditSpecs] = useState<MultipleSelect>({
    key: newParam.get('key') || '',
    value: newParam.get('value') || '',
  });
  const [keyErr, setKeyErr] = useState<string>('');
  const [valueErr, setValueErr] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'key') setKeyErr('');
    if (name === 'value') setValueErr('');
    setEditSpecs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const edit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editSpecs.key && !editSpecs.value) {
      setKeyErr(
        `${language_data?.[authState.lang]?.common_key || 'Key'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
      setValueErr(
        `${language_data?.[authState.lang]?.common_value || 'Value'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
      return;
    }
    if (!editSpecs.key) {
      return setKeyErr(
        `${language_data?.[authState.lang]?.common_key || 'Key'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
    }
    if (!editSpecs.key.trim()) {
      return setKeyErr(
        `${language_data?.[authState.lang]?.common_please || 'Please'} ${language_data?.[authState.lang]?.common_write || 'write'
        } ${language_data?.[authState.lang]?.common_something || 'something'} ${language_data?.[authState.lang]?.common_in || 'in'
        } ${language_data?.[authState.lang]?.common_key || 'key'}`
      );
    }
    if (!editSpecs.value) {
      return setValueErr(
        `${language_data?.[authState.lang]?.common_value || 'Value'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
    }
    if (!editSpecs.value.trim()) {
      return setValueErr(
        `${language_data?.[authState.lang]?.common_please || 'Please'} ${language_data?.[authState.lang]?.common_write || 'write'
        } ${language_data?.[authState.lang]?.common_something || 'something'} ${language_data?.[authState.lang]?.common_in || 'in'
        } ${language_data?.[authState.lang]?.common_value || 'value'}`
      );
    }

    const items = {
      _id: match?.params.specification_id,
      product_id: match?.params.id,
      key: editSpecs.key.trim(),
      value: editSpecs.value.trim(),
      language: 'ENGLISH',
    };

    setLoading(true);
    try {
      const apiRes = await henceforthApi.Product.editProductDetailsDynamically(
        commonArray.productOtherDetails[1],
        items
      );
      toastMessage(apiRes.message || 'Specification updated successfully');
      window.history.back();
    } catch (error) {
      handleError(error, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 sm:p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3.5 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0 shadow-2xs">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight m-0">
                {language_data?.[authState.lang]?.common_edit || 'Edit'}{' '}
                {language_data?.[authState.lang]?.common_specifications || 'Product Specification'}
              </h2>
              <p className="text-xs text-slate-500 font-medium m-0 mt-0.5">
                Update technical specification attribute key-value pair
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={edit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-xs sm:text-sm font-bold text-slate-700 tracking-wide">
                  {language_data?.[authState.lang]?.common_key || 'Specification Key'}{' '}
                  <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="key"
                  value={editSpecs.key}
                  onChange={handleChange}
                  placeholder="e.g. Material, Dimensions, Weight"
                  className={`w-full px-4 py-2.5 rounded-xl border text-xs sm:text-sm font-medium transition-all focus:outline-none focus:ring-2 ${keyErr
                      ? 'border-rose-300 bg-rose-50/30 text-rose-900 focus:ring-rose-200 focus:border-rose-500'
                      : 'border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]'
                    }`}
                />
                {keyErr && (
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 pt-1">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>{keyErr}</span>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs sm:text-sm font-bold text-slate-700 tracking-wide">
                  {language_data?.[authState.lang]?.common_value || 'Specification Value'}{' '}
                  <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="value"
                  value={editSpecs.value}
                  onChange={handleChange}
                  placeholder="e.g. 100% Cotton, 30x40 cm, 450g"
                  className={`w-full px-4 py-2.5 rounded-xl border text-xs sm:text-sm font-medium transition-all focus:outline-none focus:ring-2 ${valueErr
                      ? 'border-rose-300 bg-rose-50/30 text-rose-900 focus:ring-rose-200 focus:border-rose-500'
                      : 'border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]'
                    }`}
                />
                {valueErr && (
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 pt-1">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>{valueErr}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => window.history.back()}
                disabled={loading}
              >
                {language_data?.[authState.lang]?.common_cancel || 'Cancel'}
              </Button>
              <Button
                type="submit"
                variant="default"
                size="sm"
                disabled={loading}
                className="gap-1.5"
              >
                {loading ? (
                  <Spinner color="text-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{language_data?.[authState.lang]?.common_edit || 'Update Specification'}</span>
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default EdiSpecification;